import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { toast } from "@/lib/notify";
import API from "@/lib/api";
import { Plus, Search, MoreHorizontal, UserCog, Copy, RefreshCw, UserX, UserCheck, Pencil, Shield } from "lucide-react";
import { format } from "date-fns";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Pagination, paginate } from "@/components/Pagination";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL, roleLabel } from "@/lib/role-labels";

const ROLE_STYLES = {
  super_admin: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20",
  admin: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  org_admin: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20",
  investor: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  prospective_client: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
};

const TYCHI_MODULES = [
  { key: "tychi_portal", label: "Tychi Portal" },
  { key: "tychi_bank", label: "Tychi Bank" },
  { key: "tychi_vault", label: "Tychi Vault" },
  { key: "tychi_allocator", label: "Tychi Allocator" },
  { key: "tychi_recon", label: "Tychi Recon" },
  { key: "tychi_command", label: "Tychi Command" },
  { key: "tychi_gl", label: "Tychi GL" },
  { key: "tychi_radar", label: "Tychi Radar" },
];

const STATUS_STYLES = {
  active: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  inactive: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
};

// Normalize any raw user-sync status (Radar / GL / Recon) into one of the
// five user-facing labels the UI standardises on — Synced / In Progress /
// Failed / No Access / Not Available. Radar writes "success", GL/Recon
// write "synced"; both collapse to "Synced" here so all three columns
// read the same. "skipped" / "not_applicable" (i.e. this module doesn't
// apply to this user's role or org isn't assigned to it) become
// "No Access"; a null/missing value becomes "Not Available". This is
// display-only — retry conditions and mutations still branch on the raw
// status field, so no sync logic is affected.
const userSyncDisplay = (status) => {
  if (status === null || status === undefined || status === "") {
    return { label: "Not Available", className: "bg-muted/30 text-muted-foreground border-border/50" };
  }
  const raw = String(status).toLowerCase();
  if (raw === "synced" || raw === "success") {
    return { label: "Synced", className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" };
  }
  if (raw === "failed" || raw === "error") {
    return { label: "Failed", className: "bg-red-500/10 text-red-400 border-red-500/20" };
  }
  if (raw === "pending" || raw === "retrying" || raw === "queued" || raw === "in_progress") {
    return { label: "In Progress", className: "bg-amber-500/10 text-amber-400 border-amber-500/20" };
  }
  if (raw === "skipped" || raw === "not_applicable" || raw === "no_access") {
    return { label: "No Access", className: "bg-slate-500/10 text-slate-400 border-slate-500/20" };
  }
  return { label: "Not Available", className: "bg-muted/30 text-muted-foreground border-border/50" };
};

// Fixed GL "GL Super Admin" role — not org-specific, valid across all orgs.
// Auto-assigned whenever the user's role is super_admin.
const SUPER_ADMIN_GL_ROLE_ID = "74a3d80d-47e7-46e0-97da-06eef7247783";
const SUPER_ADMIN_GL_ROLE_NAME = "GL Super Admin";

export default function AdminManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [funds, setFunds] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [orgFilter, setOrgFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "admin", org_id: "", fund_ids: [], investor_id: "", investor_access_level: "full_access", gl_role_id: "", fund_id: "", allowed_modules: [], radar_access: false, radar_role_id: "", radar_role_name: "", recon_access: false, recon_role: "", recon_role_id: "", recon_role_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [ownerDialog, setOwnerDialog] = useState(null);
  const [newOwnerId, setNewOwnerId] = useState("");
  const [radarRoles, setRadarRoles] = useState([]);
  const [reconRoles, setReconRoles] = useState([]);
  const [glRoles, setGlRoles] = useState([]);
  const [glRolesLoading, setGlRolesLoading] = useState(false);
  const [glRolesError, setGlRolesError] = useState("");

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await API.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFunds = useCallback(async () => {
    try {
      const { data } = await API.get("/funds");
      setFunds(data);
    } catch { /* ignore */ }
  }, []);

  const fetchOrgs = useCallback(async () => {
    try {
      const { data } = await API.get("/organizations");
      setOrgs(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchUsers(); fetchFunds(); fetchOrgs(); }, [fetchUsers, fetchFunds, fetchOrgs]);

  useEffect(() => {
    API.get("/radar-roles").then(({ data }) => setRadarRoles(data || [])).catch(() => {});
    // Recon roles are seeded server-side at boot; call is safe & fast.
    API.get("/master/recon-roles").then(({ data }) => setReconRoles(data || [])).catch(() => {});
  }, []);

  // Fetch GL roles when org_id changes in the form.
  // Skip for super_admin — they always get the fixed GL Super Admin role.
  //
  // Non-super_admin target role: pull from the SAME live source the Roles &
  // Permissions page uses (`/roles-permissions/roles/org/{id}`) so freshly
  // created custom roles show up immediately with no 5-min cache lag. That
  // endpoint is super_admin-only, so if the current logged-in user is an
  // org_admin we transparently fall back to `/master/gl-roles?org_id=...`
  // (which is org_admin-accessible via GL portal proxy). The dropdown data
  // shape is normalized to { id, name } either way.
  useEffect(() => {
    if (!form.org_id || !dialogOpen || form.role === "super_admin") { setGlRoles([]); setGlRolesError(""); return; }
    let cancelled = false;
    setGlRolesLoading(true);
    setGlRolesError("");

    const fromRolesPermPayload = (data) => {
      const arr = Array.isArray(data) ? data : (data?.data || data?.roles || []);
      return arr.map((r) => ({ id: r.role_id || r.id, name: r.role_name || r.name }));
    };
    const fromGlProxyPayload = (data) => (Array.isArray(data) ? data : []);

    // Prefer the roles-permissions endpoint (complete + fresh); on 403
    // (org_admin caller) fall back to /master/gl-roles.
    API.get(`/roles-permissions/roles/org/${form.org_id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setGlRoles(fromRolesPermPayload(data));
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          API.get(`/master/gl-roles?org_id=${form.org_id}`)
            .then(({ data }) => {
              if (cancelled) return;
              setGlRoles(fromGlProxyPayload(data));
            })
            .catch(() => {
              if (cancelled) return;
              setGlRoles([]); setGlRolesError("GL roles unavailable");
            });
        } else {
          setGlRoles([]); setGlRolesError("GL roles unavailable");
        }
      })
      .finally(() => { if (!cancelled) setGlRolesLoading(false); });
    return () => { cancelled = true; };
  }, [form.org_id, dialogOpen, form.role]);

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    // TY-50: wire the org filter into the rendered list. "unassigned"
    // matches rows whose org_id is missing OR points at a non-existent org.
    if (orgFilter !== "all") {
      const uOrgId = u.org_id || "";
      if (orgFilter === "unassigned") {
        const orgExists = uOrgId && orgs.some((o) => o.id === uOrgId);
        if (orgExists) return false;
      } else if (uOrgId !== orgFilter) {
        return false;
      }
    }
    if (search) {
      // Trim leading/trailing whitespace before matching.
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    }
    return true;
  });

  const { sorted: sortedUsers, sortKey, sortDir, toggleSort } = useSortableTable(filtered);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const { paginatedItems: pagedUsers, totalPages: usersTotalPages, safePage: usersSafePage } = paginate(sortedUsers, page, pageSize);

  const openCreate = () => {
    if (orgs.length === 0) {
      toast.error("Please create an Organization before adding Users");
      return;
    }
    setEditUser(null);
    setForm({ name: "", email: "", role: "admin", org_id: "", fund_ids: [], investor_id: "", investor_access_level: "full_access", gl_role_id: "", fund_id: "", allowed_modules: [], radar_access: false, radar_role_id: "", radar_role_name: "", recon_access: false, recon_role: "", recon_role_id: "", recon_role_name: "" });
    setInvestors([]);
    setTempPassword("");
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, role: u.role, org_id: u.org_id || "", fund_ids: u.fund_ids || [], investor_id: "", gl_role_id: u.gl_role_id || "", fund_id: (u.fund_ids || [])[0] || "", allowed_modules: u.allowed_modules || [], radar_access: !!u.radar_role_id, radar_role_id: u.radar_role_id || "", radar_role_name: u.radar_role_name || "", recon_access: !!(u.recon_role_id || u.recon_role), recon_role: u.recon_role || "", recon_role_id: u.recon_role_id || "", recon_role_name: u.recon_role_name || "" });
    setTempPassword("");
    setDialogOpen(true);
  };

  const fetchInvestorsForFund = async (fundId) => {
    if (!fundId) { setInvestors([]); return; }
    try {
      const { data } = await API.get(`/investors?fund_id=${fundId}`);
      setInvestors(data);
    } catch {
      setInvestors([]);
    }
  };

  const handleFundChangeForInvestor = (fundId) => {
    // Keep the new user's name/email; only reset the investor selection.
    setForm({ ...form, fund_id: fundId, fund_ids: fundId ? [fundId] : [], investor_id: "" });
    fetchInvestorsForFund(fundId);
  };

  const handleInvestorSelect = (investorId) => {
    // Map to the EXISTING investor without overwriting the new user's
    // name/email — the related login has its own identity.
    setForm({ ...form, investor_id: investorId });
  };
  const selectedInvestor = investors.find((i) => i.id === form.investor_id) || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    if (!editUser && !form.org_id) { toast.error("Organization is required"); return; }
    if (!editUser && form.role === "admin" && form.fund_ids.length === 0) {
      toast.error(`${ROLE_LABEL.admin} must be assigned to at least one fund`);
      return;
    }
    if (!editUser && form.role === "investor" && !form.investor_id) {
      toast.error("Select an existing investor to grant access to");
      return;
    }
    if (form.role === "org_admin" && (!form.allowed_modules || form.allowed_modules.length === 0)) {
      toast.error("Admin must have at least one module selected");
      return;
    }
    if (!editUser && form.radar_access && !form.radar_role_id) {
      toast.error("Radar Role is required when Radar access is enabled");
      return;
    }
    // Recon Role validation mirrors Radar — only enforced when the caller
    // ticked "Grant access to Recon". Requires either the UUID (preferred)
    // or the legacy string.
    if (form.recon_access && !form.recon_role_id && !form.recon_role) {
      toast.error("Recon Role is required when Recon access is enabled");
      return;
    }
    if (!editUser && form.role === "super_admin") {
      // Super Admin needs no fund or investor selection — just name and email
    }
    // GL Role: super_admin always gets the fixed GL Super Admin role;
    // otherwise keep the dropdown selection.
    const glRoleIdTrimmed = form.role === "super_admin" ? SUPER_ADMIN_GL_ROLE_ID : (form.gl_role_id || "").trim();
    setSubmitting(true);
    try {
      const formPayload = {
        ...form,
        gl_role_id: glRoleIdTrimmed,
        // Backend UserUpdate.fund_ids expects List[str]. Guard against a legacy
        // string value ever slipping through (e.g. a Mongo row that stored a
        // single fund id as a scalar) so the API doesn't 422 on validation.
        fund_ids: Array.isArray(form.fund_ids)
          ? form.fund_ids
          : (form.fund_ids ? [form.fund_ids] : []),
      };
      if (form.role !== "org_admin") delete formPayload.allowed_modules;
      if (form.role !== "investor") delete formPayload.fund_id;
      if (editUser) {
        await API.put(`/users/${editUser.id}`, formPayload);
        toast.success("User updated");
      } else {
        const payload = { ...formPayload, suppress_email: form.role === "prospective_client" };
        const { data } = await API.post("/users", payload);
        setTempPassword(data.temp_password);
        toast.success(form.role === "prospective_client" ? "Prospective client invited" : "User created");
      }
      await fetchUsers();
      if (editUser) setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const [confirmAction, setConfirmAction] = useState(null);

  const toggleStatus = async (u) => {
    try {
      if (u.status === "active") {
        await API.delete(`/users/${u.id}`);
        toast.success("User deactivated");
      } else {
        await API.put(`/users/${u.id}/reactivate`);
        toast.success("User reactivated");
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await API.put(`/users/${u.id}/reset-password`);
      toast.success(`New password: ${data.temp_password}`, { duration: 10000 });
      navigator.clipboard.writeText(data.temp_password);
    } catch (err) {
      toast.error("Failed to reset password");
    }
  };

  const superAdmins = users.filter(u => u.role === "super_admin" && u.status === "active");

  const changeFmOwner = async () => {
    if (!ownerDialog || !newOwnerId) return;
    try {
      await API.patch(`/users/${ownerDialog.id}/owner?new_owner_id=${newOwnerId}`);
      toast.success("Owner updated");
      setOwnerDialog(null);
      setNewOwnerId("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change owner");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground text-sm mt-1">Create and manage platform users</p>
        </div>
        <Button onClick={openCreate} className="shadow-[0_0_20px_rgba(108,71,196,0.2)]" data-testid="create-user-button" disabled={orgs.length === 0}>
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>
      {orgs.length === 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-400" data-testid="no-orgs-warning">
          Please create an <strong>Organization</strong> before adding Users. Head over to{" "}
          <a href="/super-admin/organizations" className="underline font-medium">Organizations</a> to create one.
        </div>
      )}

      <Card data-testid="users-table-card">
        <CardHeader className="p-6 pb-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-secondary/50 h-10"
                data-testid="user-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40 bg-secondary/50 h-10" data-testid="role-filter-select">
                <SelectValue placeholder="Filter role" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">{ROLE_LABEL.admin}s</SelectItem>
                <SelectItem value="org_admin">{ROLE_LABEL.org_admin}s</SelectItem>
                <SelectItem value="investor">Investors</SelectItem>
                <SelectItem value="super_admin">Super Admins</SelectItem>
                <SelectItem value="prospective_client">Prospective Clients</SelectItem>
              </SelectContent>
            </Select>
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger className="w-44 bg-secondary/50 h-10" data-testid="org-filter-select">
                <SelectValue placeholder="Filter organization" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Organizations</SelectItem>
                <SelectItem value="unassigned" data-testid="org-filter-unassigned">Unassigned / No organization</SelectItem>
                {orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <SortableHead sortKey="name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Name</SortableHead>
                <SortableHead sortKey="email" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Email</SortableHead>
                <SortableHead sortKey="role" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Role</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Organization</TableHead>
                <SortableHead sortKey="status" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Status</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Fund</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Owner</TableHead>
                <SortableHead sortKey="created_at" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Created</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Radar</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">GL</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Recon</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={12} className="pl-6"><div className="h-10 bg-secondary/30 rounded animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                  <TableRow>
                  <TableCell colSpan={12} className="text-center text-muted-foreground py-12">
                    {search || roleFilter !== "all" || orgFilter !== "all" ? "No users match the filters" : "No users yet. Create one to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                sortedUsers.length > 0 && pagedUsers.map((u) => (
                  <TableRow key={u.id} className="table-row-interactive" data-testid={`user-row-${u.id}`}>
                    <TableCell className="pl-6 font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${ROLE_STYLES[u.role] || ""}`}>
                        {ROLE_LABEL[u.role] || u.role?.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.org_name || (u.org_id ? <span className="font-mono text-[10px]">{u.org_id.slice(0, 8)}…</span> : "—")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_STYLES[u.status] || ""}`}>
                        {u.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.fund_ids?.length > 0
                        ? funds.filter(f => u.fund_ids.includes(f.id)).map(f => f.name).join(", ") || "—"
                        : "—"
                      }
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {(u.role === "admin" || u.role === "investor") && u.owner ? (
                        <span className="text-xs">{u.owner.name}</span>
                      ) : (u.role === "admin" || u.role === "investor") ? (
                        <span className="text-xs text-muted-foreground/50">—</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.created_at ? format(new Date(u.created_at), "MMMM d, yyyy") : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${userSyncDisplay(u.radar_sync_status).className}`}
                        title={u.radar_sync_error || ""}
                        data-testid={`user-radar-status-${u.id}`}
                      >
                        {userSyncDisplay(u.radar_sync_status).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${userSyncDisplay(u.gl_sync_status).className}`}
                        title={u.gl_sync_error || ""}
                        data-testid={`user-gl-status-${u.id}`}
                      >
                        {userSyncDisplay(u.gl_sync_status).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${userSyncDisplay(u.recon_sync_status).className}`}
                        title={u.recon_sync_status === "failed" ? (u.recon_sync_error || "") : (u.recon_sync_status === "skipped" ? (u.recon_sync_error || "Not required") : "")}
                        data-testid={`user-recon-status-${u.id}`}
                      >
                        {userSyncDisplay(u.recon_sync_status).label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right pr-6">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="w-8 h-8" data-testid={`user-actions-${u.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border w-48">
                            <DropdownMenuItem onClick={() => openEdit(u)}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => resetPassword(u)}>
                              <RefreshCw className="w-4 h-4 mr-2" /> Reset Password
                            </DropdownMenuItem>
                            {(u.role === "admin" || u.role === "investor") && u.owner_id === currentUser?.id && (
                              <DropdownMenuItem onClick={() => { setOwnerDialog(u); setNewOwnerId(u.owner_id || ""); }}>
                                <Shield className="w-4 h-4 mr-2" /> Change Owner
                              </DropdownMenuItem>
                            )}
                            {u.radar_sync_status === "failed" && (
                              <DropdownMenuItem onClick={async () => {
                                try {
                                  await API.post(`/users/${u.id}/sync-radar`);
                                  toast.success("Radar sync queued");
                                  setTimeout(fetchUsers, 2000);
                                } catch { toast.error("Failed"); }
                              }}>
                                <RefreshCw className="w-4 h-4 mr-2" /> Retry Radar sync
                              </DropdownMenuItem>
                            )}
                            {u.role !== "super_admin" && u.gl_sync_status !== "synced" && u.gl_sync_status !== "not_applicable" && (
                              <DropdownMenuItem
                                onClick={async () => {
                                  try {
                                    await API.post(`/users/${u.id}/sync-gl`);
                                    toast.success("GL sync queued");
                                    setTimeout(fetchUsers, 2000);
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Failed to queue GL sync");
                                  }
                                }}
                                data-testid={`user-retry-gl-${u.id}`}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" /> Retry GL sync
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => setConfirmAction(u)}
                              className={u.status === "active" ? "text-red-400 focus:text-red-400" : "text-emerald-400 focus:text-emerald-400"}
                            >
                              {u.status === "active" ? <UserX className="w-4 h-4 mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
                              {u.status === "active" ? "Deactivate" : "Reactivate"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {!loading && sortedUsers.length > 0 && (
            <div className="px-6 pb-4">
              <Pagination currentPage={usersSafePage} totalPages={usersTotalPages} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); setTempPassword(""); }}>
        <DialogContent className="bg-card border-border sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserCog className="w-5 h-5 text-primary" />
              {editUser ? "Edit User" : "Create New User"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {editUser ? "Update user details below." : "Create a new platform user. A temporary password will be generated."}
            </DialogDescription>
          </DialogHeader>
          {tempPassword ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4">
                <p className="text-sm font-medium text-emerald-400 mb-1">User created successfully</p>
                <p className="text-xs text-muted-foreground mb-3">Share these credentials with the user:</p>
                <div className="flex items-center gap-2 bg-secondary/50 rounded-md p-3">
                  <code className="text-sm font-mono flex-1" data-testid="temp-password-display">{tempPassword}</code>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="w-8 h-8"
                    onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success("Copied!"); }}
                    data-testid="copy-password-button"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setDialogOpen(false); setTempPassword(""); }} data-testid="close-dialog-button">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary/50" data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-secondary/50" data-testid="user-email-input" />
              </div>
              {!editUser && (
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => { setForm({ ...form, role: v, fund_ids: [], fund_id: "", investor_id: "", investor_access_level: "full_access", name: "", email: "" }); setInvestors([]); }}>
                    <SelectTrigger className="bg-secondary/50" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="admin">{ROLE_LABEL.admin}</SelectItem>
                      <SelectItem value="org_admin">{ROLE_LABEL.org_admin}</SelectItem>
                      <SelectItem value="investor">Investor</SelectItem>
                      <SelectItem value="prospective_client">Prospective Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!editUser && form.role === "prospective_client" && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-500" data-testid="prospective-helper">
                  No welcome email will be sent. After creation, copy the temporary password from the success screen and share it with the client manually. They will have restricted access until you approve their onboarding submission.
                </div>
              )}
              {!editUser && (
                <div className="space-y-2">
                  <Label>Organization *</Label>
                  <Select value={form.org_id} onValueChange={(v) => setForm({ ...form, org_id: v })}>
                    <SelectTrigger className={`bg-secondary/50 ${!form.org_id ? "border-amber-500/50" : ""}`} data-testid="user-org-select">
                      <SelectValue placeholder="Select an organization" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {orgs.map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                      {orgs.length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No organizations. Create one first.</div>
                      )}
                    </SelectContent>
                  </Select>
                  {!form.org_id && <p className="text-xs text-amber-400">Required — every user must belong to an organization</p>}
                </div>
              )}
              {!editUser && form.role === "admin" && (
                <div className="space-y-2">
                  <Label>Assign to Fund *</Label>
                  <Select value={form.fund_ids[0] || ""} onValueChange={(v) => setForm({ ...form, fund_ids: [v] })}>
                    <SelectTrigger className={`bg-secondary/50 ${form.fund_ids.length === 0 ? "border-amber-500/50" : ""}`} data-testid="user-fund-select">
                      <SelectValue placeholder="Select a fund" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {funds.filter(f => f.status !== "closed").map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name} <span className="text-muted-foreground ml-1 text-xs">({f.status})</span></SelectItem>
                      ))}
                      {funds.filter(f => f.status !== "closed").length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No funds available. Create a fund first.</div>
                      )}
                    </SelectContent>
                  </Select>
                  {form.fund_ids.length === 0 && <p className="text-xs text-amber-400">Required — {ROLE_LABEL.admin} must be linked to a fund</p>}
                </div>
              )}
              {!editUser && form.role === "investor" && (
                <>
                  <div className="space-y-2">
                    <Label>Fund <span className="text-rose-500">*</span></Label>
                    <Select value={form.fund_id || ""} onValueChange={handleFundChangeForInvestor}>
                      <SelectTrigger className="bg-secondary/50" data-testid="investor-fund-select">
                        <SelectValue placeholder="Select a fund" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {funds.filter(f => f.status !== "closed" && (!form.org_id || f.org_id === form.org_id)).map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                        {funds.filter(f => !form.org_id || f.org_id === form.org_id).length === 0 && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No funds in this organization. Create a fund first.</div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Existing Investor <span className="text-rose-500">*</span></Label>
                    <Select value={form.investor_id || ""} onValueChange={handleInvestorSelect} disabled={!form.fund_id}>
                      <SelectTrigger className={`bg-secondary/50 ${!form.investor_id ? "border-amber-500/50" : ""}`} data-testid="investor-select">
                        <SelectValue placeholder={form.fund_id ? "Select an investor" : "Select a fund first"} />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {investors.map((i) => (
                          <SelectItem key={i.id} value={i.id}>{i.name} <span className="text-muted-foreground ml-1 text-xs">({i.email})</span></SelectItem>
                        ))}
                        {form.fund_id && investors.length === 0 && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No investors in this fund.</div>
                        )}
                      </SelectContent>
                    </Select>
                    {!form.investor_id && <p className="text-xs text-amber-400">Required — select the investor to grant access to</p>}
                  </div>
                  {selectedInvestor && (
                    <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2 text-sm" data-testid="selected-investor-panel">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{selectedInvestor.name}</span>
                        <span className="text-xs text-muted-foreground">{selectedInvestor.email}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs" data-testid="selected-investor-kyc">KYC:&nbsp;<strong>{selectedInvestor.kyc_status || "—"}</strong></span>
                        <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs" data-testid="selected-investor-onboarding">Onboarding:&nbsp;<strong>{selectedInvestor.onboarding_status || "—"}</strong></span>
                      </div>
                      {(selectedInvestor.related_users || []).length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">Existing related users:</span>
                          <ul className="mt-1 space-y-0.5">
                            {selectedInvestor.related_users.map((ru, idx) => (
                              <li key={idx} data-testid="related-user-row">• {ru.name} ({ru.email}) — {ru.access_level === "view_only" ? "View Only" : "Full Access"}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="text-[11px] text-emerald-500">Grants login access to this existing investor. No new investor record or KYC is created.</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Access Level <span className="text-rose-500">*</span></Label>
                    <Select value={form.investor_access_level || "full_access"} onValueChange={(v) => setForm({ ...form, investor_access_level: v })}>
                      <SelectTrigger className="bg-secondary/50" data-testid="investor-access-level-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="full_access">Full Access</SelectItem>
                        <SelectItem value="view_only">View Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">View Only can see the investor data but cannot upload, edit, or transact. Full Access mirrors the primary investor.</p>
                  </div>
                </>
              )}
              {form.role === "org_admin" && (
                <div className="space-y-2" data-testid="org-admin-modules-block">
                  <Label>Module Access <span className="text-rose-500">*</span></Label>
                  <p className="text-[11px] text-muted-foreground -mt-1">Fundtec Fund Accounting — choose Tychi apps this admin can use.</p>
                  <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-3 bg-secondary/30">
                    {TYCHI_MODULES.map((m) => {
                      const checked = (form.allowed_modules || []).includes(m.key);
                      return (
                        <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="rounded border-border text-primary focus:ring-primary"
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? form.allowed_modules.filter((x) => x !== m.key)
                                : [...(form.allowed_modules || []), m.key];
                              const updates = { allowed_modules: next };
                              if (m.key === "tychi_radar" && checked) {
                                updates.radar_access = false;
                                updates.radar_role_id = "";
                                updates.radar_role_name = "";
                              }
                              if (m.key === "tychi_recon" && checked) {
                                updates.recon_access = false;
                                updates.recon_role = "";
                                updates.recon_role_id = "";
                                updates.recon_role_name = "";
                              }
                              setForm({ ...form, ...updates });
                            }}
                            data-testid={`module-checkbox-${m.key}`}
                          />
                          <span>{m.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Admin is scoped to one organization and only the modules selected here.</p>
                </div>
              )}
              {/* Radar Access — only visible when Tychi Radar module is selected */}
              {!editUser && (form.allowed_modules || []).includes("tychi_radar") && (
                <div className="space-y-3 rounded-lg border border-border/50 p-4 bg-secondary/10" data-testid="radar-access-block">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-semibold">Grant access to Radar</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">Sync this user to Tychi Radar for monitoring and alerts</p>
                    </div>
                    <input
                      type="checkbox"
                      className="rounded border-border text-primary focus:ring-primary w-4 h-4"
                      checked={form.radar_access}
                      onChange={(e) => setForm({ ...form, radar_access: e.target.checked, ...(!e.target.checked ? { radar_role_id: "", radar_role_name: "" } : {}) })}
                      data-testid="radar-access-checkbox"
                    />
                  </div>
                  {form.radar_access && (
                    <div className="space-y-2">
                      <Label>Radar Role <span className="text-rose-500">*</span></Label>
                      <Select value={form.radar_role_id} onValueChange={(v) => {
                        const role = radarRoles.find(r => r.id === v);
                        setForm({ ...form, radar_role_id: v, radar_role_name: role?.name || "" });
                      }}>
                        <SelectTrigger className={`bg-secondary/50 ${!form.radar_role_id ? "border-amber-500/50" : ""}`} data-testid="radar-role-select">
                          <SelectValue placeholder="Select Radar role" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {radarRoles.map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!form.radar_role_id && <p className="text-xs text-amber-400">Required when Radar access is enabled</p>}
                    </div>
                  )}
                </div>
              )}
              {/* Recon Access — mirrors the Radar block. Only visible when
                  Tychi Recon module is selected. Role is a fixed 3-option
                  dropdown (admin | analyst | viewer) matching what Recon
                  accepts on POST /api/v1/sync/user. */}
              {(form.allowed_modules || []).includes("tychi_recon") && (
                <div className="space-y-3 rounded-lg border border-border/50 p-4 bg-secondary/10" data-testid="recon-access-block">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-semibold">Grant access to Recon</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">Sync this user to Tychi Recon for reconciliation workflows</p>
                    </div>
                    <input
                      type="checkbox"
                      className="rounded border-border text-primary focus:ring-primary w-4 h-4"
                      checked={form.recon_access}
                      onChange={(e) => setForm({ ...form, recon_access: e.target.checked, ...(!e.target.checked ? { recon_role: "", recon_role_id: "", recon_role_name: "" } : {}) })}
                      data-testid="recon-access-checkbox"
                    />
                  </div>
                  {form.recon_access && (
                    <div className="space-y-2">
                      <Label>Recon Role <span className="text-rose-500">*</span></Label>
                      <Select
                        value={form.recon_role_id}
                        onValueChange={(v) => {
                          const role = reconRoles.find((r) => r.id === v);
                          setForm({
                            ...form,
                            recon_role_id: v,
                            recon_role_name: role?.name || "",
                            // Keep legacy string in lock-step for
                            // backwards-compatible reads.
                            recon_role: (role?.name || "").toLowerCase(),
                          });
                        }}
                      >
                        <SelectTrigger className={`bg-secondary/50 ${!form.recon_role_id ? "border-amber-500/50" : ""}`} data-testid="recon-role-select">
                          <SelectValue placeholder="Select Recon role" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {reconRoles.length === 0 ? (
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">No Recon roles synced yet</div>
                          ) : (
                            reconRoles.map((r) => (
                              <SelectItem
                                key={r.id}
                                value={r.id}
                                data-testid={`recon-role-option-${(r.name || "").toLowerCase()}`}
                              >
                                {r.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {!form.recon_role_id && <p className="text-xs text-amber-400">Required when Recon access is enabled</p>}
                    </div>
                  )}
                </div>
              )}
              {/* GL Role — dropdown populated from GL API. super_admin always
                  gets the fixed, org-agnostic GL Super Admin role. */}
              {(() => {
                const selectedOrgId = editUser ? (editUser.org_id || form.org_id) : form.org_id;
                const selectedOrg = orgs.find((o) => o.id === selectedOrgId);
                const orgSynced = selectedOrg?.gl_sync_status === "synced";
                const isSuperAdmin = form.role === "super_admin";
                // super_admin is fixed & valid for all orgs → not gated on org sync.
                const disabled = isSuperAdmin ? false : !orgSynced;
                return (
                  <div className="space-y-2" data-testid="gl-role-id-block">
                    <Label>GL Role <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                    <Select
                      value={isSuperAdmin ? SUPER_ADMIN_GL_ROLE_ID : (form.gl_role_id || "")}
                      onValueChange={(v) => setForm({ ...form, gl_role_id: v })}
                      disabled={disabled}
                    >
                      <SelectTrigger className={`bg-secondary/50 ${disabled ? "opacity-50" : ""}`} data-testid="user-gl-role-select">
                        <SelectValue placeholder={glRolesLoading ? "Loading roles..." : glRolesError || "Select GL role (optional)"} />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {isSuperAdmin ? (
                          <SelectItem value={SUPER_ADMIN_GL_ROLE_ID}>{SUPER_ADMIN_GL_ROLE_NAME}</SelectItem>
                        ) : (
                          glRoles.map(r => (
                            <SelectItem key={r.id || r.role_id} value={r.id || r.role_id}>
                              {r.name || r.role_name || r.id || r.role_id}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {isSuperAdmin && (
                      <p className="text-xs text-muted-foreground" data-testid="super-admin-auto-roles-hint">
                        Super admins are automatically assigned the GL Super Admin role (works across all organizations) and auto-synced to Tychi Radar with the Admin role — no manual selection needed.
                      </p>
                    )}
                    {!orgSynced && !isSuperAdmin && (
                      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-500" data-testid="gl-role-org-not-synced-warning">
                        Organization is not yet synced to GL. GL Role cannot be assigned until org is synced.
                      </div>
                    )}
                    {glRolesError && orgSynced && !isSuperAdmin && (
                      <p className="text-xs text-amber-400">{glRolesError}</p>
                    )}
                  </div>
                );
              })()}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="user-submit-button">
                  {submitting ? "Saving..." : editUser ? "Update" : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(v) => { if (!v) setConfirmAction(null); }}
        title={confirmAction?.status === "active" ? "Deactivate User" : "Reactivate User"}
        description={confirmAction?.status === "active"
          ? `Are you sure you want to deactivate "${confirmAction?.name}"? They will lose access to the platform.`
          : `Are you sure you want to reactivate "${confirmAction?.name}"? They will regain access to the platform.`}
        confirmLabel={confirmAction?.status === "active" ? "Deactivate" : "Reactivate"}
        variant={confirmAction?.status === "active" ? "destructive" : "default"}
        onConfirm={() => { toggleStatus(confirmAction); setConfirmAction(null); }}
      />

      {/* Change Owner Dialog */}
      <Dialog open={!!ownerDialog} onOpenChange={(v) => { if (!v) { setOwnerDialog(null); setNewOwnerId(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="change-owner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" /> Change Owner
            </DialogTitle>
            <DialogDescription>
              Reassign ownership of <strong>{ownerDialog?.name}</strong> to another Super Admin.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 mb-2">
            <p className="text-xs text-amber-400 font-medium">Caution: You will no longer be able to manage ownership of this {ownerDialog?.role === "admin" ? ROLE_LABEL.admin : "Investor"} after the transfer.</p>
          </div>
          <div className="space-y-3 py-2">
            <Label className="text-xs">New Owner (Super Admin)</Label>
            <Select value={newOwnerId} onValueChange={setNewOwnerId}>
              <SelectTrigger className="bg-secondary/50" data-testid="new-owner-select"><SelectValue placeholder="Select Super Admin" /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {superAdmins.map(sa => (
                  <SelectItem key={sa.id} value={sa.id}>{sa.name} ({sa.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOwnerDialog(null); setNewOwnerId(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={changeFmOwner} disabled={!newOwnerId || newOwnerId === ownerDialog?.owner_id} data-testid="confirm-owner-change-btn">Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
